import React, { useState } from 'react'
import clsx from 'clsx'
import { isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'

import { IconArrowDown } from 'components/Icons'
import { Row } from 'components/Table'
import { setFormatDate } from 'helpers/common'

import type { ProductHistory } from 'features/Ticket'
import type { IColumn } from 'types'

interface IProductHistorySingleProps {
  history: ProductHistory
  columns: IColumn[]
  render: (id: string) => JSX.Element[] | JSX.Element | undefined
}

interface IProductHistorySingleState {
  id: string
  isShow: boolean
}

const TicketProductHistorySingle: React.FC<IProductHistorySingleProps> = (
  props: IProductHistorySingleProps,
) => {
  const { columns, history, render } = props
  const { t } = useTranslation('ticket')

  const [openCollapsible, setOpenCollapsible] =
    useState<IProductHistorySingleState>({
      id: '',
      isShow: false,
    })

  const base = 'f-ticket-product-history-modal'
  const isChecklist = !isEmpty(history.checklist)
  const isOpenChecklist = openCollapsible.isShow && isChecklist

  const list: Record<string, React.ReactNode> = {
    taskId: history.task.id,
    code: history.task.code,
    completedAt: history.task.completedAt
      ? setFormatDate(new Date(history.task.completedAt))
      : '-',
    issueType: history.task.type.nameTh,
    detail: (
      <div className={`${base}__icon-layout`}>
        <div
          className={clsx(
            `${base}__history-detail`,
            isChecklist && `${base}__history-detail--collapse`,
          )}
        >
          {history.detail || '-'}
        </div>
        {isChecklist && (
          <IconArrowDown
            className={clsx(
              `${base}__icon ${base}__collapsible`,
              isOpenChecklist && `${base}__icon--active`,
            )}
            variants='xs'
          />
        )}
      </div>
    ),
  }

  return (
    <Row
      data-testid='row-product-history-single'
      key={history.task.id}
      columns={columns}
      data={list}
      onClick={() =>
        setOpenCollapsible({
          id: history.task.id,
          isShow: !isOpenChecklist,
        })
      }
    >
      {isOpenChecklist && (
        <tr className={`${base}__list-row`}>
          <td colSpan={columns.length}>
            <div className={`${base}__collapsible-content`}>
              <div className={`${base}__collapsible-header`}>
                <span className={`${base}__collapsible--text`}>
                  {t('checklist')}
                </span>
              </div>

              {render(history.task.id)}

              <div className={`${base}__collapsible-footer`}>
                <div className={`${base}__collapsible-footer-item`}>
                  <span className={`${base}__collapsible-footer--title`}>
                    {t('requestDate.title')}
                  </span>
                  <span className={`${base}__collapsible-footer--value`}>
                    {!isEmpty(history.task.requestDate)
                      ? setFormatDate(new Date(history.task.requestDate))
                      : '-'}
                  </span>
                </div>

                <div className={`${base}__collapsible-footer-item`}>
                  <span className={`${base}__collapsible-footer--title`}>
                    {t('assignTo.title')}
                  </span>
                  <span className={`${base}__collapsible-footer--value`}>
                    {history.userFullName || '-'}
                  </span>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </Row>
  )
}

export default TicketProductHistorySingle
